import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Outcome badge meta — order also defines legend/filter order
const OUTCOME_META = {
  executed:           { label: "Executed",          color: "#4ade80", bg: "rgba(74,222,128,0.14)",  border: "rgba(74,222,128,0.35)" },
  skipped_decision:   { label: "Skipped · Decision",color: "#60a5fa", bg: "rgba(96,165,250,0.14)",  border: "rgba(96,165,250,0.35)" },
  skipped_bp:         { label: "Skipped · BP",      color: "#fbbf24", bg: "rgba(251,191,36,0.14)",  border: "rgba(251,191,36,0.35)" },
  rejected_preflight: { label: "Rejected · Preflight", color: "#f97316", bg: "rgba(249,115,22,0.14)", border: "rgba(249,115,22,0.35)" },
  failed:             { label: "Failed",            color: "#ef4444", bg: "rgba(239,68,68,0.14)",   border: "rgba(239,68,68,0.35)" },
};

const fmtAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

function DecisionRow({ d }) {
  const [open, setOpen] = useState(false);
  const [simBusy, setSimBusy] = useState(false);
  const [sim, setSim] = useState(null);
  const meta = OUTCOME_META[d.outcome] || { label: d.outcome || "?", color: "#8b949e", bg: "rgba(139,148,158,0.12)", border: "rgba(139,148,158,0.3)" };

  const simulate = async (e) => {
    e.stopPropagation();
    setSimBusy(true);
    setSim(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const r = await axios.post(`${API}/autopilot/decisions/${d.decision_id}/simulate`, {}, { signal: controller.signal });
      setSim(r.data);
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
        setSim({ ok: false, verdict: "error", error: "Simulation timed out after 12s — market data may be unavailable." });
      } else {
        setSim({ ok: false, verdict: "error", error: err.response?.data?.detail || err.message });
      }
    } finally {
      clearTimeout(timer);
      setSimBusy(false);
    }
  };

  const verdictMeta = {
    "would-execute":      { label: "Would execute", color: "#4ade80", bg: "rgba(74,222,128,0.14)" },
    "bp-too-low":         { label: "BP too low now", color: "#fbbf24", bg: "rgba(251,191,36,0.14)" },
    "price-moved":        { label: "Price moved >25%", color: "#f97316", bg: "rgba(249,115,22,0.14)" },
    "preflight-now-fails":{ label: "Preflight now fails", color: "#ef4444", bg: "rgba(239,68,68,0.14)" },
    "quote-unavailable":  { label: "Quote unavailable", color: "#8b949e", bg: "rgba(139,148,158,0.14)" },
    "error":              { label: "Sim error", color: "#ef4444", bg: "rgba(239,68,68,0.14)" },
  };

  return (
    <div
      data-testid={`autopilot-decision-${d.decision_id}`}
      onClick={() => setOpen(o => !o)}
      style={{
        background: "var(--layer2)", borderRadius: 10, padding: 12, marginBottom: 8,
        borderLeft: `3px solid ${meta.color}`, cursor: "pointer",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#e6edf3" }}>{d.ticker}</span>
            {d.strike != null && (
              <span style={{ fontSize: 11, color: "#60a5fa" }}>${d.strike} {String(d.optionType || "").toUpperCase()}</span>
            )}
            <span style={{
              padding: "2px 8px", borderRadius: 4, fontSize: 9, fontWeight: 700,
              background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
            }}>{meta.label}</span>
            {d.ziScore != null && (
              <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 3, background: "rgba(167,139,250,0.15)", color: "#a78bfa", fontWeight: 700 }}>
                Zi {d.ziScore}
              </span>
            )}
          </div>
          <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 4 }}>{d.reason}</div>
        </div>
        <div style={{ fontSize: 10, color: "var(--dim)", whiteSpace: "nowrap" }}>{fmtAgo(d.createdAt)}</div>
      </div>

      {/* Expandable detail */}
      {open && (
        <div data-testid={`autopilot-decision-detail-${d.decision_id}`} style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 11, color: "#c9d1d9", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px" }}>
          <div><span style={{ color: "var(--dim)" }}>Decision:</span> {d.decisionStatus || "—"}</div>
          <div><span style={{ color: "var(--dim)" }}>Mid price:</span> ${Number(d.mid ?? 0).toFixed(2)}</div>
          <div><span style={{ color: "var(--dim)" }}>ZI class:</span> {d.ziClass || "—"}</div>
          <div><span style={{ color: "var(--dim)" }}>Account:</span> ${Number(d.accountSize ?? 0).toFixed(0)}</div>

          {/* Risk gate */}
          <div style={{ gridColumn: "1 / -1", marginTop: 4 }}>
            <span style={{ color: "var(--dim)" }}>Risk Gate:</span>{" "}
            <span style={{ color: d.riskGate?.passed ? "#4ade80" : "#f87171" }}>
              {d.riskGate?.passed ? "PASSED" : "BLOCKED"}
            </span>
            {d.riskGate?.aiOverride && <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 3, background: "rgba(167,139,250,0.18)", color: "#a78bfa", fontSize: 9, fontWeight: 700 }}>AI OVERRIDE</span>}
            {d.riskGate?.reasons && (
              <div style={{ marginTop: 2, fontSize: 10, color: "#c9d1d9", fontStyle: "italic" }}>
                {Array.isArray(d.riskGate.reasons) ? d.riskGate.reasons.join(" · ") : String(d.riskGate.reasons)}
              </div>
            )}
          </div>

          {/* Sizing */}
          {d.sizing && (
            <div style={{ gridColumn: "1 / -1", padding: 8, background: "rgba(255,255,255,0.02)", borderRadius: 6, marginTop: 4 }}>
              <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 4, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>Sizing</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 4, fontSize: 11 }}>
                <div>BP: <strong>${Number(d.sizing.buyingPower ?? 0).toFixed(2)}</strong></div>
                <div>Max %: <strong>{Math.round((d.sizing.maxPct ?? 0) * 100)}%</strong></div>
                <div>Max spend: <strong>${Number(d.sizing.maxSpend ?? 0).toFixed(2)}</strong></div>
                <div>Contract cost: <strong>${Number(d.sizing.contractCost ?? 0).toFixed(2)}</strong></div>
                <div>Qty: <strong>{d.sizing.qty}</strong></div>
              </div>
            </div>
          )}

          {/* Preflight */}
          {d.preflight && (
            <div style={{ gridColumn: "1 / -1", padding: 8, background: "rgba(96,165,250,0.06)", borderRadius: 6, marginTop: 4 }}>
              <div style={{ fontSize: 9, color: "#60a5fa", marginBottom: 4, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>Public.com Preflight</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 4, fontSize: 11 }}>
                <div>Order value: <strong>${Number(d.preflight.orderValue ?? 0).toFixed(2)}</strong></div>
                <div>Commission: <strong>${Number(d.preflight.commission ?? 0).toFixed(2)}</strong></div>
                {d.preflight.buyingPowerReq != null && <div>BP req: <strong>${Number(d.preflight.buyingPowerReq).toFixed(2)}</strong></div>}
                {d.preflight.estimatedQuantity != null && <div>Est qty: <strong>{d.preflight.estimatedQuantity}</strong></div>}
              </div>
            </div>
          )}

          {/* Order / error */}
          {d.orderId && <div style={{ gridColumn: "1 / -1", fontSize: 10, color: "var(--dim)", fontFamily: "monospace", marginTop: 4 }}>Order ID: {d.orderId}</div>}
          {d.error && <div style={{ gridColumn: "1 / -1", padding: 8, background: "rgba(239,68,68,0.08)", borderRadius: 6, color: "#f87171", fontSize: 11, marginTop: 4 }}>{d.error}</div>}

          {/* Re-simulate against live market data */}
          <div style={{ gridColumn: "1 / -1", marginTop: 6, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <button
              data-testid={`resim-btn-${d.decision_id}`}
              onClick={simulate}
              disabled={simBusy}
              style={{
                padding: "6px 12px", borderRadius: 16, border: "1px solid rgba(167,139,250,0.35)",
                background: simBusy ? "#3a3f47" : "rgba(167,139,250,0.12)",
                color: "#a78bfa", fontSize: 10, fontWeight: 700,
                cursor: simBusy ? "default" : "pointer",
              }}
            >{simBusy ? "Simulating…" : (sim ? "Re-simulate" : "Simulate with live data")}</button>

            {sim && (() => {
              const vm = verdictMeta[sim.verdict] || verdictMeta.error;
              return (
                <div data-testid={`sim-result-${d.decision_id}`} style={{ marginTop: 8, padding: 10, background: vm.bg, borderRadius: 8, border: `1px solid ${vm.color}33`, fontSize: 11, color: "#c9d1d9" }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 4, color: vm.color, background: vm.bg, border: `1px solid ${vm.color}55` }}>
                      {vm.label}
                    </span>
                    {sim.simulatedAt && <span style={{ fontSize: 10, color: "var(--dim)" }}>{fmtAgo(sim.simulatedAt)}</span>}
                  </div>
                  {sim.reason && <div style={{ marginBottom: 6, fontStyle: "italic", color: "#c9d1d9" }}>{sim.reason}</div>}
                  {sim.error && <div style={{ marginBottom: 6, color: "#f87171" }}>{sim.error}</div>}

                  {sim.current && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                      <div>
                        <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 2 }}>Mid now</div>
                        <div style={{ fontWeight: 700, color: "#e6edf3" }}>${Number(sim.current.mid ?? 0).toFixed(2)}</div>
                        {sim.delta?.midPct != null && (
                          <div style={{ fontSize: 10, color: Math.abs(sim.delta.midPct) > 10 ? "#fbbf24" : "#8b949e" }}>
                            {sim.delta.midPct > 0 ? "+" : ""}{sim.delta.midPct}% vs then
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 2 }}>BP now</div>
                        <div style={{ fontWeight: 700, color: "#e6edf3" }}>${Number(sim.current.bp ?? 0).toFixed(2)}</div>
                        <div style={{ fontSize: 10, color: "var(--dim)" }}>was ${sim.original?.bp != null ? Number(sim.original.bp).toFixed(2) : d.sizing?.buyingPower != null ? Number(d.sizing.buyingPower).toFixed(2) : "—"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 2 }}>Qty now</div>
                        <div style={{ fontWeight: 700, color: "#e6edf3" }}>{sim.current.qty}</div>
                        {sim.delta?.qty != null && sim.delta.qty !== 0 && (
                          <div style={{ fontSize: 10, color: sim.delta.qty > 0 ? "#4ade80" : "#f87171" }}>
                            {sim.delta.qty > 0 ? "+" : ""}{sim.delta.qty} vs then
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {sim.current?.preflight && (
                    <div style={{ marginTop: 8, padding: 6, background: "rgba(96,165,250,0.06)", borderRadius: 4, fontSize: 10 }}>
                      Preflight: value <strong>${Number(sim.current.preflight.orderValue ?? 0).toFixed(2)}</strong>
                      {" · "}commission <strong>${Number(sim.current.preflight.commission ?? 0).toFixed(2)}</strong>
                      {sim.current.preflight.buyingPowerReq != null && <> · BP req <strong>${Number(sim.current.preflight.buyingPowerReq).toFixed(2)}</strong></>}
                    </div>
                  )}
                  {sim.current?.preflightError && (
                    <div style={{ marginTop: 6, padding: 6, background: "rgba(239,68,68,0.08)", borderRadius: 4, fontSize: 10, color: "#f87171" }}>
                      Preflight error: {sim.current.preflightError}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AutopilotDecisionReplay() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [overview, setOverview] = useState(null);

  const load = () => {
    const params = filter ? `?outcome=${filter}&limit=50` : "?limit=50";
    axios.get(`${API}/autopilot/decisions${params}`).then(r => setData(r.data)).catch(() => {});
    axios.get(`${API}/autopilot/decisions/stats/overview?days=7`).then(r => setOverview(r.data)).catch(() => {});
  };

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const outcomes = Object.keys(OUTCOME_META);

  return (
    <div className="card" data-testid="autopilot-decision-replay" style={{ borderColor: "rgba(167,139,250,0.25)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
        <div className="sh" style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
          Autopilot Decision Replay
          <span className="tag t-pur" style={{ fontSize: 8 }}>AUDIT TRAIL</span>
        </div>
        {overview && (
          <div style={{ fontSize: 10, color: "var(--dim)" }} data-testid="autopilot-decision-overview">
            Last 7d: <strong style={{ color: "#e6edf3" }}>{overview.total}</strong> decisions · <strong style={{ color: "#4ade80" }}>{overview.executionRate}%</strong> executed
          </div>
        )}
      </div>

      {/* Outcome filter chips */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <button
          data-testid="decision-filter-all"
          onClick={() => setFilter("")}
          style={{
            padding: "4px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700, border: "1px solid " + (filter === "" ? "#a78bfa" : "rgba(255,255,255,0.08)"),
            background: filter === "" ? "rgba(167,139,250,0.15)" : "transparent",
            color: filter === "" ? "#a78bfa" : "var(--dim)", cursor: "pointer",
          }}
        >All {data ? `· ${data.total}` : ""}</button>
        {outcomes.map(o => {
          const count = data?.summary?.[o] ?? 0;
          const m = OUTCOME_META[o];
          const active = filter === o;
          return (
            <button
              key={o}
              data-testid={`decision-filter-${o}`}
              onClick={() => setFilter(o)}
              style={{
                padding: "4px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                border: `1px solid ${active ? m.color : "rgba(255,255,255,0.08)"}`,
                background: active ? m.bg : "transparent",
                color: active ? m.color : "var(--dim)", cursor: "pointer",
              }}
            >{m.label} · {count}</button>
          );
        })}
      </div>

      {!data ? (
        <div style={{ textAlign: "center", padding: 20, color: "var(--dim)", fontSize: 12 }}>
          <span className="spin" style={{ marginRight: 8 }} /> Loading decisions...
        </div>
      ) : data.decisions.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: "var(--dim)", fontSize: 12 }}>
          {filter ? `No ${OUTCOME_META[filter]?.label.toLowerCase() || filter} decisions yet` : "No autopilot decisions yet. Enable FULL_AUTO mode to see the trail."}
        </div>
      ) : (
        <div data-testid="autopilot-decision-list">
          {data.decisions.map((d) => <DecisionRow key={d.decision_id} d={d} />)}
        </div>
      )}
    </div>
  );
}
